import type { OrganizationId } from '@/lib/organization-scoping'
import { useOpenCashDrawers } from './cash-drawers.query'
import { refreshManualTenderDrawer } from './cash-drawers.preflight'
import { useManualTenderDrawer } from './useManualTenderDrawer'

interface RefundCashDrawerOptions {
  organizationId: OrganizationId
  locationId: number
  canOperateCashDrawers: boolean
  enabled: boolean
}

/**
 * The till a cash or check refund comes from: this register's paired till, or the only open till at the order's
 * location. `resolve` answers null when Cash Drawer tracking is off; `drawer` is the register as last read, to show.
 */
export function useRefundCashDrawer({
  organizationId,
  locationId,
  canOperateCashDrawers,
  enabled,
}: RefundCashDrawerOptions) {
  const drawer = useManualTenderDrawer({
    organizationId,
    targetLocationId: locationId,
    canOperateCashDrawers,
    enabled,
  })
  const openDrawersQuery = useOpenCashDrawers({
    enabled: enabled && drawer.trackingEnabled && canOperateCashDrawers,
  })

  const resolve = async (): Promise<number | null> => {
    // Whether the shop tracks tills is read, never assumed off (strictStatus): a status unavailable or unread
    // blocks the handoff, since cash handed back without its till is a refund the server refuses.
    const result = await refreshManualTenderDrawer({
      allowSoleOpenSession: true,
      strictStatus: true,
      canOperateCashDrawers,
      currentDrawerQuery: drawer.currentDrawerQuery,
      organizationId,
      openDrawersQuery,
      pairedWorkstation: drawer.pairedWorkstation,
      statusQuery: drawer.statusQuery,
      targetLocationId: locationId,
      workstationId: drawer.workstationId,
      workstationsQuery: drawer.workstationsQuery,
    })

    return result.session?.id ?? null
  }

  return { drawer, resolve }
}
